'use client';

import { cn } from '@/lib/utils';
import { useTheme, type Theme } from './ThemeProvider';

const SEGMENTS: ReadonlyArray<{ value: Theme; label: string }> = [
  { value: 'booth', label: 'Booth' },
  { value: 'daylight', label: 'Daylight' },
];

/**
 * Compact 2-segment theme pill for the topbar (prototype R3Toggle). Active
 * segment uses the neutral-inverse pill tokens (never accent). Renders nothing
 * when the THEME_TOGGLE flag is off.
 *
 * The segments override the global 44px min button size (min-h-0/min-w-0) to
 * keep the 24px-tall pill anatomy from the design.
 */
export default function ThemeToggle() {
  const { theme, setTheme, enabled } = useTheme();
  if (!enabled) return null;

  return (
    <div
      role="group"
      aria-label="Color theme"
      className="flex items-center gap-[2px] rounded-full border border-border-subtle bg-pill-bg p-[3px]"
    >
      {SEGMENTS.map(({ value, label }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            aria-pressed={active}
            className={cn(
              'inline-flex h-6 min-h-0 min-w-0 items-center rounded-full px-[11px] text-[11.5px] font-semibold transition-colors',
              active
                ? 'bg-pill-active-bg text-pill-active-text'
                : 'bg-transparent text-text-tertiary hover:text-text-secondary'
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
